import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { StitchedPMark } from './StitchedPMark';
import { Avatar } from './Avatar';
import { useSession } from '../lib/auth';
import { usePendingInbox } from '../lib/useEnrollment';
import { useViewingAs } from '../lib/useViewingAs';

type NavItem = { id: string; label: string; to: string | null; icon: ReactNode };

const ITEMS: NavItem[] = [
  { id: 'home', label: 'Camp', to: '/', icon: <path d="M3 21 L12 4 L21 21 Z M9 14 H15 V21 H9 Z" /> },
  {
    id: 'browse',
    label: 'Patches',
    to: '/patches',
    icon: (
      <g>
        <circle cx="12" cy="12" r="8" />
        <circle cx="12" cy="12" r="4" />
      </g>
    ),
  },
  { id: 'mine', label: 'Sashes', to: '/sash', icon: <g><path d="M7 3 L17 3 L14 21 L10 21 Z" /></g> },
  {
    id: 'family',
    label: 'Family',
    to: '/family',
    icon: (
      <g>
        <circle cx="9" cy="8" r="3" />
        <circle cx="17" cy="10" r="2.5" />
        <path d="M3 20 C3 16 6 14 9 14 C12 14 15 16 15 20" />
        <path d="M14 20 C14 17 16 16 18 16 C20 16 21 17 21 20" />
      </g>
    ),
  },
];

export function DesktopSidebar() {
  const location = useLocation();
  const { profile } = useSession();
  const { data: inbox } = usePendingInbox();
  const inboxCount = inbox?.length ?? 0;
  const { withAs } = useViewingAs();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '20px 0' }}>
      <div style={{ padding: '0 20px 24px', borderBottom: '1px solid rgba(241,231,208,0.15)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <StitchedPMark size={42} />
          <div>
            <div
              style={{
                fontFamily: 'Bevan',
                fontSize: 18,
                color: 'var(--cream)',
                letterSpacing: '0.04em',
                lineHeight: 1,
              }}
            >
              PATCH LEGACY
            </div>
            <div
              style={{
                fontFamily: 'JetBrains Mono',
                fontSize: 9,
                color: 'var(--gold)',
                letterSpacing: '0.1em',
                marginTop: 4,
              }}
            >
              ★ FAMILY TRADITION
            </div>
          </div>
        </div>
      </div>

      <nav
        style={{
          flex: 1,
          padding: '20px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        {ITEMS.map((t) => {
          const isActive = t.to !== null && (
            t.to === '/'
              ? location.pathname === '/'
              : location.pathname === t.to || location.pathname.startsWith(t.to + '/')
          );
          const isDisabled = t.to === null;
          const baseStyle: React.CSSProperties = {
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 14px',
            background: isActive ? 'rgba(241,231,208,0.1)' : 'none',
            border: 'none',
            borderLeft: isActive ? '3px solid var(--gold)' : '3px solid transparent',
            color: isActive ? 'var(--gold)' : isDisabled ? 'rgba(241,231,208,0.35)' : 'rgba(241,231,208,0.7)',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            textAlign: 'left',
            fontFamily: 'Bevan',
            fontSize: 13,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            textDecoration: 'none',
          };
          const inner = (
            <>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                {t.icon}
              </svg>
              {t.label}
            </>
          );
          if (isDisabled) {
            return (
              <div key={t.id} style={baseStyle} aria-disabled="true">
                {inner}
              </div>
            );
          }
          return (
            <Link key={t.id} to={withAs(t.to!)} style={baseStyle}>
              {inner}
            </Link>
          );
        })}
      </nav>

      <Link
        to={withAs('/inbox')}
        style={{
          margin: '0 12px 8px',
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          background: inboxCount > 0 ? 'rgba(212,168,70,0.15)' : 'transparent',
          color: inboxCount > 0 ? 'var(--gold, #D4A846)' : 'rgba(241,231,208,0.55)',
          border: '1px solid ' + (inboxCount > 0 ? 'var(--gold, #D4A846)' : 'rgba(241,231,208,0.2)'),
          textDecoration: 'none',
          fontFamily: 'JetBrains Mono',
          fontSize: 11,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        <span style={{ flex: 1 }}>Inbox</span>
        {inboxCount > 0 && (
          <span
            style={{
              background: 'var(--brick)',
              color: 'var(--cream)',
              minWidth: 20,
              height: 20,
              padding: '0 6px',
              borderRadius: 10,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'Bevan',
              fontSize: 10,
              border: '1.5px solid var(--ink)',
            }}
          >
            {inboxCount}
          </span>
        )}
      </Link>

      <Link
        to={withAs('/patches/new')}
        style={{
          margin: '0 12px 8px',
          padding: '12px 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          background: 'var(--brick)',
          color: 'var(--cream)',
          border: '1.5px solid var(--ink)',
          textDecoration: 'none',
          fontFamily: 'Bevan',
          fontSize: 12,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          boxShadow: '2px 2px 0 var(--ink)',
        }}
      >
        + Design a Patch
      </Link>

      {profile && (
        <Link
          to="/profile"
          style={{
            margin: '0 12px 4px',
            padding: '10px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            border: '1px solid rgba(241,231,208,0.2)',
            background: 'rgba(241,231,208,0.04)',
            color: 'var(--cream)',
            textDecoration: 'none',
          }}
        >
          <Avatar
            url={profile.avatar_url}
            name={profile.display_name}
            color={profile.member_color || '#1E3A2A'}
            size={32}
            borderColor="var(--cream)"
          />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontFamily: 'Bevan',
                fontSize: 13,
                color: 'var(--cream)',
                letterSpacing: '0.04em',
                lineHeight: 1.1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {profile.display_name}
            </div>
            <div
              style={{
                fontFamily: 'JetBrains Mono',
                fontSize: 9,
                color: 'rgba(241,231,208,0.55)',
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                marginTop: 3,
              }}
            >
              View Profile
            </div>
          </div>
        </Link>
      )}
    </div>
  );
}
